import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { schoolService } from '@modules/staff/schools/services/schoolService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import useFormHook from '@shared/hooks/useFormHook';

export const useSchoolForm = ({ school, onClose, selectedData, fetchData }) => {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [serverMessage, setServerMessage] = useState(null);

    const validationSchema = yup.object({
        name: yup.string()
            .required(t('validation.institution.name.required'))
            .max(255, t('validation.institution.name.maxLength')),
    });

    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
        defaultValues: school
            ? {
                id: school.id,
                shortName: school.shortName,
                name: school.name,
                country: school.country,
                postcode: school.postcode,
                address1: school.address1,
                address2: school.address2,
                active: Boolean(school.active)
            }
            : {
                shortName: '',
                name: '',
                country: '',
                postcode: '',
                address1: '',
                address2: '',
                active: true
            }
    });

    const watchedValues = watch();

    const isActiveChecked = watch('active', Boolean(school?.active));

    const handleUpdateSchool = useCallback(async (data) => {
        try {
            await schoolService.updateSchool(selectedData, data);
            notifySuccess(t('nav.schools.form.successUpdate'));
            return true;
        } catch (error) {
            notifyError(t('nav.schools.form.errorUpdate'));
            return false;
        }
    }, [selectedData]);

    const handleCreateSchool = useCallback(async (data) => {
        try {
            await schoolService.createSchool(data);
            notifySuccess(t('nav.schools.form.successCreate'));
            return true;
        } catch (error) {
            notifyError(t('nav.schools.form.errorCreate'));
            return false;
        }
    }, []);

    const onSubmit = useCallback(async (data) => {
        try {
            // Transform data to send countryCode instead of country
            const transformedData = {
                ...data,
                countryCode: data.country // Send countryCode instead of country
            };
            
            const success = school 
                ? await handleUpdateSchool(transformedData)
                : await handleCreateSchool(transformedData);

            if (success) {
                fetchData(schoolService.getSchools, { sortBy: 'name', sortDir: 'asc' });
                onClose();
            }
        } catch (error) {
            console.error('Error saving school:', error);
            setServerMessage(error.response?.data?.message || 'Failed to save school');
        }
    }, [school, handleUpdateSchool, handleCreateSchool, fetchData, onClose]);

    const handleActiveChange = useCallback((e) => {
        const newValue = e.target.checked;
        setValue('active', newValue);
    }, [setValue]);

    return {
        modalRef,
        register,
        handleSubmit,
        errors,
        setValue,
        watch,
        isActiveChecked,
        serverMessage,
        onSubmit,
        handleActiveChange
    };
}; 